import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, RefreshCw, Key, AlertCircle, CheckCircle } from 'lucide-react';
import { useSenutoToken } from '@/hooks/useSenutoToken';

const SenutoTokenPanel = () => {
  const {
    token,
    isLoading,
    error,
    fetchToken,
    copyToClipboard
  } = useSenutoToken();

  const handleCopyToken = async () => {
    if (token) {
      const success = await copyToClipboard(token.token);
      if (success) {
        // Można dodać toast notification
        console.log('Token skopiowany do schowka');
      }
    }
  };

  const handleRefresh = () => {
    fetchToken();
  };

  const getStatusBadge = () => {
    if (!token) return null;
    
    return (
      <Badge variant="secondary" className="flex items-center gap-1 text-green-600">
        <CheckCircle className="h-3 w-3" />
        Aktywny
      </Badge>
    );
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="space-y-4">
        {/* Informacja na początku */}
        <div className="text-center bg-muted/50 rounded-lg p-6 mb-6 min-h-[120px] flex items-center justify-center">
          <p className="text-sm text-foreground font-medium">
            Aktualny token API Senuto pobierany z bazy danych. Token jest automatycznie aktualizowany przez n8n workflow.
          </p>
        </div>

        {/* Przycisk odświeżania */}
        <div className="flex justify-center">
          <Button 
            onClick={handleRefresh}
            disabled={isLoading}
            variant="outline"
            className="w-full"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Odświeżanie...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Odśwież Token
              </>
            )}
          </Button>
        </div>

        {/* Błąd */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Błąd</span>
            </div>
            <p className="text-sm text-destructive/80 mt-1">{error}</p>
          </div>
        )}

        {/* Token */}
        {token && (
          <div className="bg-card border rounded-lg p-6 w-full min-h-[200px]">
            <div className="text-center mb-5">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="p-2 rounded bg-indigo-500 text-white">
                  <Key className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">
                  Senuto API Token
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                {getStatusBadge()}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 p-3 bg-muted border rounded font-mono text-sm text-center break-all">
                    {token.token}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyToken}
                    className="px-3 py-3"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  Token automatycznie aktualizowany przez n8n workflow
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Brak tokenu */}
        {!token && !isLoading && !error && (
          <div className="bg-muted/50 border rounded-lg p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                Brak aktywnego tokenu
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Kliknij "Odśwież Token" aby pobrać najnowszy token z bazy danych
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SenutoTokenPanel;
